/**
 * Warehouse FQN → friendly label resolver (frontend).
 *
 * When a connector replicates its data into our Redshift warehouse, the
 * LLM-generated SQL references tables by their dlt-normalized fully-qualified
 * names — e.g. `tenant_61d0…f86.airtable_appm_wa8p_l3k49_n_gs_s__tbl_u1ci…`.
 * Those names are unreadable. This module pulls the
 * `{warehouse_fqn → LabelEntry}` map from the backend
 * (`GET /api/v1/tenants/{tenant_id}/warehouse-labels`) and offers a pure
 * helper that substitutes the friendly path into a raw SQL string for
 * display only.
 *
 * Design notes:
 *  - **Display-only.** `substituteSqlLabels` is never used to rewrite SQL
 *    that gets sent back to the backend for execution. Real SQL stays raw.
 *  - **Single source of truth.** Backend builds the map from the latest
 *    Schema per replicating connector — same shape as the Connector Detail
 *    schema explorer reads, just keyed differently. So labels stay in sync
 *    with whatever the user sees in the schema panel.
 *  - **Refresh.** Listens to the `klaris:sync-job-finished` event the global
 *    SyncJobProvider already dispatches; that's the only time the map can
 *    change (apart from a rename in Airtable, which the user will trigger
 *    a sync after). No polling.
 */
import { useCallback, useEffect, useState } from 'react';
import { buildApiUrl } from '../config';

// ── Types ────────────────────────────────────────────────────────────────

export interface WarehouseLabelEntry {
  /** Short, leaf-only label e.g. "Deals" */
  label: string;
  /** Full path including connector display name, e.g. ["ODP Airtable", "Sales CRM", "Deals"] */
  breadcrumb: string[];
  /** Pre-joined breadcrumb e.g. "ODP Airtable › Sales CRM › Deals" */
  qualified: string;
  connector_id: string;
  connector_name: string;
  connector_type: string;
  /** Warehouse FQN (same as map key, for convenience). */
  entity_id: string;
}

export type WarehouseLabelMap = Record<string, WarehouseLabelEntry>;

interface ApiResponse {
  labels: WarehouseLabelMap;
  count: number;
}

// ── Pure helper: substitution ────────────────────────────────────────────

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Replace every warehouse FQN (and its bare-table form) in `rawSql` with the
 * matching friendly label, double-quoted. The result is *not* meant to be
 * executable — it's a render-layer transformation.
 *
 * Algorithm:
 *  1. Sort FQNs by length DESC so longer names are processed first
 *     (avoids prefix collisions like `tenant_x.foo` swallowing
 *     `tenant_x.foo_bar`).
 *  2. For each FQN, replace the schema-qualified form (`schema.table`)
 *     using a word-boundary regex.
 *  3. Then replace the bare-table form (`table` without schema prefix),
 *     for queries executed with `search_path` set on Redshift.
 *
 * Returns the original SQL unchanged when the map is empty or the SQL has
 * no matches.
 */
export function substituteSqlLabels(rawSql: string, labels: WarehouseLabelMap): string {
  if (!rawSql || !labels) return rawSql;
  const fqns = Object.keys(labels);
  if (fqns.length === 0) return rawSql;

  fqns.sort((a, b) => b.length - a.length);

  let out = rawSql;
  for (const fqn of fqns) {
    const entry = labels[fqn];
    if (!entry) continue;
    const friendly = `"${entry.qualified}"`;

    // 1) Fully-qualified form: tenant_xxx.airtable_…__tbl_…
    //
    // (?<![\w]) — not preceded by a word char (so we don't chop into a
    //             longer identifier like `xxxtenant_xxx.…`).
    // (?![\w])  — not followed by a word char (same reason on the right).
    const fqnRe = new RegExp(`(?<![\\w])${escapeRegex(fqn)}(?![\\w])`, 'g');
    out = out.replace(fqnRe, friendly);

    // 2) Bare-table form (no schema prefix). Used when search_path is set
    //    so the query says `FROM airtable_…__tbl_…` directly. Skip when
    //    the FQN has no dot (defensive — shouldn't happen in practice).
    const dotIdx = fqn.indexOf('.');
    if (dotIdx > 0) {
      const bare = fqn.slice(dotIdx + 1);
      // (?<![\w.]) excludes the case where a schema prefix is present
      // (already handled by case 1).
      const bareRe = new RegExp(`(?<![\\w.])${escapeRegex(bare)}(?![\\w])`, 'g');
      out = out.replace(bareRe, friendly);
    }
  }
  return out;
}

/**
 * Resolve a friendly LabelEntry for either form of a table reference:
 *  - full FQN  e.g. `tenant_x.airtable_…__tbl_…`
 *  - bare name e.g. `airtable_…__tbl_…`  (what the ontology / Data Model
 *    page passes around — Connector Schema's `entity.name` is the bare form).
 *
 * Returns `null` if no match — caller falls back to the raw name.
 *
 * Linear in `Object.keys(labels)`, but realistic tenants have a few hundred
 * tables max so it's not worth caching a reverse index.
 */
export function resolveLabel(
  rawName: string | null | undefined,
  labels: WarehouseLabelMap,
): WarehouseLabelEntry | null {
  if (!rawName || !labels) return null;
  const direct = labels[rawName];
  if (direct) return direct;
  for (const fqn of Object.keys(labels)) {
    const dotIdx = fqn.indexOf('.');
    if (dotIdx > 0 && fqn.slice(dotIdx + 1) === rawName) return labels[fqn];
  }
  return null;
}

// ── Module-level cache + refresh hub ─────────────────────────────────────
//
// One labels map per tenant, shared across components so we don't duplicate
// the network call. Invalidated when a sync job completes (the only event
// that can change the map) and on tenant change.

const cache = new Map<string, WarehouseLabelMap>();
const inflight = new Map<string, Promise<WarehouseLabelMap>>();

/** Ensure all subscribers re-render when a tenant's map updates. */
type Listener = () => void;
const listeners = new Map<string, Set<Listener>>();
function subscribe(tenantId: string, l: Listener) {
  let set = listeners.get(tenantId);
  if (!set) {
    set = new Set();
    listeners.set(tenantId, set);
  }
  set.add(l);
  return () => {
    set!.delete(l);
    if (set!.size === 0) listeners.delete(tenantId);
  };
}
function notify(tenantId: string) {
  listeners.get(tenantId)?.forEach((l) => l());
}

async function fetchLabels(tenantId: string): Promise<WarehouseLabelMap> {
  const token = window.localStorage.getItem('klaris_jwt');
  const resp = await fetch(
    buildApiUrl(`/api/v1/tenants/${tenantId}/warehouse-labels`),
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!resp.ok) {
    // 404/403 etc — log once but don't throw; the UI just falls back to raw FQNs.
    console.warn('[warehouse-labels] fetch failed', resp.status);
    return {};
  }
  const data = (await resp.json()) as ApiResponse;
  return data?.labels || {};
}

function invalidate(tenantId: string) {
  cache.delete(tenantId);
  inflight.delete(tenantId);
  notify(tenantId);
}

// ── Hook ─────────────────────────────────────────────────────────────────

export interface UseWarehouseLabelsResult {
  labels: WarehouseLabelMap;
  loading: boolean;
  /** Force a refetch (e.g. after the user renames tables in Airtable). */
  refresh: () => void;
}

/**
 * Returns the warehouse-FQN→label map for the given tenant.
 *
 * Falls back to an empty map silently on any error so callers can use it
 * unconditionally and still render raw SQL when labels are missing.
 *
 * Pass `tenantId={null}` (e.g. before the tenant is hydrated from
 * localStorage) and the hook stays inert.
 */
export function useWarehouseLabels(tenantId: string | null | undefined): UseWarehouseLabelsResult {
  const [labels, setLabels] = useState<WarehouseLabelMap>(() => {
    if (!tenantId) return {};
    return cache.get(tenantId) || {};
  });
  const [loading, setLoading] = useState<boolean>(false);

  const load = useCallback((id: string) => {
    if (cache.has(id)) {
      setLabels(cache.get(id)!);
      return;
    }
    let p = inflight.get(id);
    if (!p) {
      p = fetchLabels(id).then((m) => {
        cache.set(id, m);
        inflight.delete(id);
        notify(id);
        return m;
      });
      inflight.set(id, p);
    }
    setLoading(true);
    p.then((m) => {
      setLabels(m);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, []);

  // Initial load + tenant changes.
  useEffect(() => {
    if (!tenantId) {
      setLabels({});
      return;
    }
    load(tenantId);
    const unsub = subscribe(tenantId, () => {
      const m = cache.get(tenantId) || {};
      setLabels(m);
    });
    return unsub;
  }, [tenantId, load]);

  // Refresh when a sync completes — that's the only time labels can change.
  // (SyncJobProvider dispatches this when an active job slot transitions
  // from running → not-running.)
  useEffect(() => {
    if (!tenantId) return;
    const handler = () => {
      invalidate(tenantId);
      load(tenantId);
    };
    window.addEventListener('klaris:sync-job-finished', handler);
    return () => window.removeEventListener('klaris:sync-job-finished', handler);
  }, [tenantId, load]);

  const refresh = useCallback(() => {
    if (!tenantId) return;
    invalidate(tenantId);
    load(tenantId);
  }, [tenantId, load]);

  return { labels, loading, refresh };
}
